import { useId, useState } from 'react'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import {
  CHOICE_TEXT_RESPONSE_LABEL,
  CHOICE_TEXT_RESPONSE_MESSAGE,
  CHOICE_TEXT_RESPONSE_PLACEHOLDER,
  choiceTextMaxCharacters,
  isChoiceTextTargetSelected,
  isChoiceTextWithinLimits,
} from '../../../lib/choice-text-response'
import type { ChoiceDraftAnswer, SurveyQuestion } from '../../../lib/survey-question.types'
import {
  surveyOtherSpecifyCounterSx,
  surveyOtherSpecifyErrorSx,
  surveyOtherSpecifyInputSx,
  surveyOtherSpecifyLabelSx,
  surveyOtherSpecifyWrapSx,
} from '../../../styles/surveyExecutionStyles'

interface OtherSpecifyFieldProps {
  readonly questionId: string
  readonly value: string
  readonly onChange: (text: string) => void
  readonly maxCharacters?: number | null
  /** Text is empty or outside min/max — message shows once the field was left. */
  readonly invalid?: boolean
}

interface ChoiceTextSpecifyProps {
  readonly question: SurveyQuestion
  readonly draft: ChoiceDraftAnswer
  readonly onChange: (value: ChoiceDraftAnswer) => void
}

/** Renders the specify field only while the question's text-enabled option is selected. */
export function ChoiceTextSpecify({ question, draft, onChange }: ChoiceTextSpecifyProps) {
  if (!isChoiceTextTargetSelected(question, draft.selectedIds)) return null
  return (
    <OtherSpecifyField
      questionId={question.id}
      value={draft.otherText}
      onChange={(otherText) => onChange({ ...draft, otherText })}
      maxCharacters={choiceTextMaxCharacters(question)}
      invalid={!isChoiceTextWithinLimits(question, draft.otherText)}
    />
  )
}

/** BFP-1156 — specify field shown under a selected text-enabled choice option. */
export function OtherSpecifyField({
  questionId,
  value,
  onChange,
  maxCharacters = null,
  invalid = false,
}: OtherSpecifyFieldProps) {
  const [touched, setTouched] = useState(false)
  const labelId = useId()
  const errorId = useId()
  const showError = touched && invalid

  return (
    <Box sx={surveyOtherSpecifyWrapSx} data-testid={`survey-q-${questionId}-specify-wrap`}>
      <Typography component="label" id={labelId} sx={surveyOtherSpecifyLabelSx}>
        {CHOICE_TEXT_RESPONSE_LABEL}
      </Typography>
      <TextField
        fullWidth
        placeholder={CHOICE_TEXT_RESPONSE_PLACEHOLDER}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => setTouched(true)}
        error={showError}
        sx={surveyOtherSpecifyInputSx}
        inputProps={{
          'data-testid': `survey-q-${questionId}-other-specify`,
          'aria-labelledby': labelId,
          'aria-invalid': showError,
          ...(showError ? { 'aria-describedby': errorId } : {}),
          ...(maxCharacters != null ? { maxLength: maxCharacters } : {}),
        }}
      />
      {showError ? (
        <Typography
          id={errorId}
          role="alert"
          sx={surveyOtherSpecifyErrorSx}
          data-testid={`survey-q-${questionId}-specify-error`}
        >
          {CHOICE_TEXT_RESPONSE_MESSAGE}
        </Typography>
      ) : null}
      {maxCharacters != null ? (
        <Typography
          sx={surveyOtherSpecifyCounterSx}
          data-testid={`survey-q-${questionId}-specify-counter`}
        >
          {`${value.length} / ${maxCharacters}`}
        </Typography>
      ) : null}
    </Box>
  )
}
